import React, { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './AddressInput.css';

/**
 * Address input with map verification: write the address, hit
 * "Locate on map", and a pin preview confirms it resolves to a real
 * place (free Nominatim geocoding, server-side). Parents gate their
 * submit on `verified`.
 */

// Dedicated delivery-pin map (NOT the shop map - the popup shows the
// full resolved address so you can confirm it's really your place).
function AddressPinMap({ pin, note = 'Your incoming cards will be shipped here.' }) {
    const divRef = useRef(null);
    const mapRef = useRef(null);

    useEffect(() => {
        if (!divRef.current || mapRef.current) return;
        const map = L.map(divRef.current, { scrollWheelZoom: false })
            .setView([pin.lat, pin.lng], 15);
        L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
            attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
            maxZoom: 19,
        }).addTo(map);
        mapRef.current = map;
        return () => { map.remove(); mapRef.current = null; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const map = mapRef.current;
        if (!map) return;
        map.setView([pin.lat, pin.lng], 15);
        // one marker, popup = the full resolved address, opened immediately
        map.eachLayer(l => { if (l instanceof L.Marker) l.remove(); });
        const icon = L.divIcon({
            className: 'addr-pin',
            // the site's pin icon (map markers need raw markup, not a component)
            html: '<svg width="30" height="30" viewBox="0 0 24 24" fill="#3b82f6" stroke="#fff" stroke-width="1.5" stroke-linejoin="round"><path d="M12 21s-7-6.1-7-11.5A7 7 0 0 1 19 9.5C19 14.9 12 21 12 21z"/><circle cx="12" cy="9.5" r="2.5" fill="#fff" stroke="none"/></svg>',
            iconSize: [30, 30],
            iconAnchor: [15, 28],
        });
        const parts = (pin.display_name || '').split(', ');
        const line1 = parts.slice(0, 2).join(', ');
        const rest = parts.slice(2).join(', ');
        // DOM nodes rather than an HTML string: the address text comes
        // from the geocoder and is displayed literally
        const pop = document.createElement('div');
        const strong = document.createElement('strong');
        strong.textContent = line1 || 'Located';
        pop.append(strong);
        if (rest) {
            const restEl = document.createElement('span');
            restEl.className = 'addr-pop-rest';
            restEl.textContent = rest;
            pop.append(document.createElement('br'), restEl);
        }
        const noteEl = document.createElement('span');
        noteEl.className = 'addr-pop-note';
        noteEl.textContent = note;
        pop.append(document.createElement('br'), noteEl);
        L.marker([pin.lat, pin.lng], { icon })
            .addTo(map)
            .bindPopup(pop, { closeButton: false })
            .openPopup();
    }, [pin]);

    return <div className="shop-map addr-pin-map" ref={divRef} style={{ height: 220 }} />;
}

function AddressInput({ address, setAddress, verified, setVerified, note, placeholder = 'Street, city, state, ZIP' }) {
    const [checking, setChecking] = useState(false);
    const [pin, setPin] = useState(null);
    const [err, setErr] = useState(null);

    const verify = async () => {
        setChecking(true);
        setErr(null);
        try {
            const res = await fetch('/api/shops/geocode', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ address }),
            });
            const json = await res.json();
            if (!res.ok) throw new Error(json.error || 'Could not locate that address.');
            setPin(json);
            setVerified(true);
        } catch (e) {
            setErr(e.message);
            setPin(null);
            setVerified(false);
        } finally {
            setChecking(false);
        }
    };

    return (
        <div className="addr-input">
            <div className="ship-addr-row">
                <input
                    type="text"
                    placeholder={placeholder}
                    value={address}
                    onChange={e => {
                        setAddress(e.target.value);
                        setVerified(false);
                        setPin(null);
                        setErr(null);
                    }}
                />
                <button
                    type="button"
                    className={`addr-verify-btn${verified ? ' verified' : ''}`}
                    onClick={verify}
                    disabled={checking || !address.trim() || verified}
                >
                    {checking ? 'Locating…' : verified ? '✓ Located' : 'Locate on map'}
                </button>
            </div>
            {err && <p className="addr-err">{err}</p>}
            {pin && (
                <div className="addr-preview">
                    <AddressPinMap pin={pin} note={note} />
                    <p className="addr-found">
                        Not your place? Adjust the address above and locate again.
                    </p>
                </div>
            )}
        </div>
    );
}

export default AddressInput;
